import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../store/store";
import { fetchGlobalSearchAsync, fetchSearchFiltersAsync } from "../../../store/thunks/GlobalSearchThunk";
import { resetSearchResults, SearchUser, SearchOrganization } from "../../../store/slices/GlobalSearchSlice";
import { STATUS } from "../../../store/slices/OrganizationSlice";
import { Pagination, PaginationPrevious, PaginationList, PaginationPage, PaginationNext } from "../../Catalyst/pagination";
import UserResultRow from "./components/UserResultRow";
import OrgResultRow from "./components/OrgResultRow";
import SearchFilters from "./components/SearchFilters";
import SearchResultsSkeleton from "./components/SearchResultsSkeleton";
import GlobalSearchIcon from "../../../assets/GlobalSearchIcon";
import CloseIcon from "../../../assets/CloseIcon";
import EmptySearchIcon from "../../../assets/EmptySearchIcon";

type TabType = "all" | "users" | "organization";

const tabs: { key: TabType; label: string }[] = [
    { key: "all", label: "All" },
    { key: "users", label: "Users" },
    { key: "organization", label: "Organization" },
];

const SELECTION_MAP: Record<TabType, string> = {
    all: "ALL",
    users: "USERS",
    organization: "ORGANIZATIONS",
};

const GlobalSearch = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [searchText, setSearchText] = useState("");
    const [activeTab, setActiveTab] = useState<TabType>("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);

    const {
        users,
        organizations,
        status,
        error,
        total_pages,
        roleOptions,
        statusOptions,
        orgFilterOptions,
    } = useSelector((state: RootState) => state.GlobalSearchStore);

    // Fetch filter options on mount
    useEffect(() => {
        dispatch(fetchSearchFiltersAsync());
        return () => {
            dispatch(resetSearchResults());
        };
    }, [dispatch]);

    // Keyboard shortcut: Cmd+K / Ctrl+K to focus search
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "k") {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
            if (e.key === "Escape") {
                setSearchText("");
                searchInputRef.current?.blur();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    const performSearch = useCallback(
        (text: string, page: number) => {
            if (text.length >= 1) {
                dispatch(
                    fetchGlobalSearchAsync({
                        page,
                        per_page: 10,
                        searchText: text,
                        selection: SELECTION_MAP[activeTab],
                        role: selectedRoles.length > 0 ? selectedRoles : undefined,
                        userStatus: selectedStatuses.length > 0 ? selectedStatuses : undefined,
                        organizationId: selectedOrgs.length > 0 ? selectedOrgs : undefined,
                    })
                );
            } else {
                dispatch(resetSearchResults());
            }
        },
        [dispatch, selectedRoles, selectedStatuses, selectedOrgs, activeTab]
    );

    // Refs to always access the latest values without stale closures
    const performSearchRef = useRef(performSearch);
    performSearchRef.current = performSearch;
    const searchTextRef = useRef(searchText);
    searchTextRef.current = searchText;

    const debouncedSearch = useMemo(
        () => debounce((text: string) => {
            setCurrentPage(1);
            performSearchRef.current(text, 1);
        }, 300),
        []
    );

    // Cancel debounce on unmount
    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedSearch(value);
    };

    const handleClearSearch = () => {
        setSearchText("");
        dispatch(resetSearchResults());
        searchInputRef.current?.focus();
    };

    // Re-search when filters or tab change
    useEffect(() => {
        const text = searchTextRef.current;
        if (text.length >= 1) {
            setCurrentPage(1);
            performSearchRef.current(text, 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRoles, selectedStatuses, selectedOrgs, activeTab]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        performSearch(searchText, page);
    };

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    const handleRoleToggle = (role: string) => {
        setSelectedRoles((prev) =>
            prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
        );
    };

    const handleStatusToggle = (status: string) => {
        setSelectedStatuses((prev) =>
            prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
        );
    };

    const handleOrgToggle = (orgId: string) => {
        setSelectedOrgs((prev) =>
            prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]
        );
    };

    const handleUserClick = (user: SearchUser) => {
        navigate(`/user-profile/${user.organization.id}/${user.id}`);
    };

    const handleOrgClick = (org: SearchOrganization) => {
        navigate(`/org-view/${org.id}`);
    };

    // Filter results by tab
    const filteredUsers = activeTab === "organization" ? [] : users;
    const filteredOrgs = activeTab === "users" ? [] : organizations;
    const hasResults = filteredUsers.length > 0 || filteredOrgs.length > 0;
    const hasSearched = searchText.length >= 1 && status !== STATUS.IDLE;

    const showRoleFilter = activeTab !== "organization";
    const showStatusFilter = activeTab !== "organization";

    return (
        <div className="bg-white pt-8 pb-12 overflow-visible rounded-tl-[40px] min-h-screen">
            {/* Search Bar */}
            <div className="flex gap-5 items-center px-8">
                <div className="flex-1">
                    <div className="relative flex items-center border border-[#22C55E] rounded-lg px-3.5 py-2.5 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_rgba(34,197,94,0.24)]">
                        <GlobalSearchIcon />
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchText}
                            onChange={handleSearchChange}
                            placeholder="Search by user (name, email, or ID) or organization (name, ID)"
                            className="w-full text-base font-medium text-texthead placeholder:text-gray-400 outline-none bg-transparent"
                        />
                    </div>
                </div>
                {searchText && (
                    <button
                        onClick={handleClearSearch}
                        className="border border-brColor rounded-lg p-3.5 hover:bg-gray-50 transition-colors"
                    >
                        <CloseIcon />
                    </button>
                )}
            </div>

            {/* Tabs and Filters */}
            {hasSearched && (
                <div className="px-8 mt-5">
                    <div className="border border-shadowborder rounded-xl shadow-light-sm">
                        {/* Tab bar + filters */}
                        <div className="flex items-center justify-between gap-3 px-6 pt-3 pb-4">
                            {/* Tabs */}
                            <div className="flex items-center gap-3">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        onClick={() => handleTabChange(tab.key)}
                                        className={`px-3 py-2 rounded-lg text-sm font-semibold shadow-light-sm transition-colors ${
                                            activeTab === tab.key
                                                ? "bg-[#22C55E] text-white border border-[#22C55E]"
                                                : "bg-white text-textColor3 border border-brColor hover:bg-gray-50"
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* Filters */}
                            <SearchFilters
                                showRoleFilter={showRoleFilter}
                                showStatusFilter={showStatusFilter}
                                selectedRoles={selectedRoles}
                                selectedStatuses={selectedStatuses}
                                selectedOrgs={selectedOrgs}
                                roleOptions={roleOptions}
                                statusOptions={statusOptions}
                                orgFilterOptions={orgFilterOptions}
                                onRoleToggle={handleRoleToggle}
                                onRoleClearAll={() => setSelectedRoles([])}
                                onStatusToggle={handleStatusToggle}
                                onStatusClearAll={() => setSelectedStatuses([])}
                                onOrgToggle={handleOrgToggle}
                                onOrgClearAll={() => setSelectedOrgs([])}
                            />
                        </div>

                        {/* Results */}
                        {status === STATUS.LOADING ? (
                            <SearchResultsSkeleton />
                        ) : status === STATUS.FAILED ? (
                            <div className="flex flex-col items-center justify-center h-[195px] gap-4">
                                <ExclamationTriangleIcon className="w-8 h-8 text-red-400" />
                                <p className="text-sm font-medium text-red-500 text-center w-[294px]">
                                    {error || "Something went wrong. Please try again."}
                                </p>
                            </div>
                        ) : hasResults ? (
                            <div>
                                {filteredUsers.map((user) => (
                                    <UserResultRow
                                        key={`user-${user.id}`}
                                        user={user}
                                        onClick={handleUserClick}
                                    />
                                ))}
                                {filteredOrgs.map((org) => (
                                    <OrgResultRow
                                        key={`org-${org.id}`}
                                        org={org}
                                        onClick={handleOrgClick}
                                    />
                                ))}
                            </div>
                        ) : (
                            /* Empty State */
                            <div className="flex flex-col items-center justify-center h-[195px] gap-4">
                                <EmptySearchIcon />
                                <p className="text-sm font-medium text-texthead opacity-50 text-center w-[294px]">
                                    There's nothing to show here. Maybe check the spelling or try another keywords
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {hasResults && total_pages > 1 && (
                        <Pagination className="mt-6">
                            <PaginationPrevious
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            />
                            <PaginationList>
                                {(() => {
                                    const pages: React.ReactNode[] = [];
                                    const addPage = (num: number) => {
                                        pages.push(
                                            <PaginationPage
                                                key={num}
                                                onClick={() => handlePageChange(num)}
                                                current={num === currentPage}
                                            >
                                                {num}
                                            </PaginationPage>
                                        );
                                    };
                                    const addEllipsis = (key: string) => {
                                        pages.push(
                                            <span key={key} className="px-1 text-sm text-zinc-500">...</span>
                                        );
                                    };

                                    if (total_pages <= 5) {
                                        for (let i = 1; i <= total_pages; i++) addPage(i);
                                    } else {
                                        addPage(1);
                                        if (currentPage > 3) addEllipsis("left");
                                        for (let p = currentPage - 1; p <= currentPage + 1; p++) {
                                            if (p > 1 && p < total_pages) addPage(p);
                                        }
                                        if (currentPage < total_pages - 2) addEllipsis("right");
                                        addPage(total_pages);
                                    }
                                    return pages;
                                })()}
                            </PaginationList>
                            <PaginationNext
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === total_pages}
                            />
                        </Pagination>
                    )}
                </div>
            )}
        </div>
    );
};

export default GlobalSearch;
